// LOAD-BEARING SIDE-EFFECT IMPORT — do not delete as "unused".
// The hub import warmed the Rematch model cluster; this specifier restores
// that init order. It retires with the Z2 codemod.
import '@/pages/api/scribeAPIV2';
// App-side form-gallery orchestration. The headless query and its wire
// encoding live behind `@heidi/templates/data`; this hook owns form atoms,
// specialty expansion, debounce, pagination reset, and app analytics.
import {
  formsPageAtom,
  formsSearchStringAtom,
  formsSelectedLocationAtom,
  formsSelectedSortAtom,
  formsSelectedSpecialtyAtom,
} from '@/atoms/formsCommunityAtoms';
import { useQueryTemplateSpecialties } from '@heidi/templates';
import { FilterSettings } from '@heidi/templates/contracts';
import { useTemplateSpecialties } from '@heidi/templates';
import { GET_FORM_TEMPLATES } from '@heidi/templates/contracts';
import {
  useQueryFormTemplates as useQueryFormTemplatesCore,
  trackCommunityTemplateSearched,
} from '@heidi/templates/data';
import { useDebounce } from '@uidotdev/usehooks';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';

export { GET_FORM_TEMPLATES };

/**
 * Query hook for fetching form templates (filtered to FORM_TEMPLATE category only).
 * Mirrors useQueryCommunityTemplates but specifically for forms.
 *
 * @param options.enabled - When false the query is disabled (useful for lazy loading, e.g. in pickers).
 */
export function useQueryFormTemplates(options?: { enabled?: boolean }) {
  const selectedSort = useAtomValue(formsSelectedSortAtom);
  const selectedLocation = useAtomValue(formsSelectedLocationAtom);
  const selectedSpecialty = useAtomValue(formsSelectedSpecialtyAtom);
  const searchString = useAtomValue(formsSearchStringAtom);
  const [page, setPage] = useAtom(formsPageAtom);

  const { data: specialtiesData } = useTemplateSpecialties(useQueryTemplateSpecialties);

  const { sort, location, specialty, search } = {
    sort: selectedSort,
    location:
      selectedLocation === FilterSettings.ALL.toString() ||
      selectedLocation === FilterSettings.UNDEFINED.toString()
        ? undefined
        : selectedLocation,
    specialty:
      selectedSpecialty === FilterSettings.ALL.toString() ||
      selectedSpecialty === FilterSettings.UNDEFINED.toString()
        ? undefined
        : selectedSpecialty,
    search: searchString === '' ? undefined : searchString,
  };

  const specialtyGroups = useMemo(() => {
    if (!specialty || !specialtiesData) return undefined;
    const matchedSpecialty = specialtiesData.find((s) => s.name === specialty);
    return matchedSpecialty?.groups?.map((g) => g.name);
  }, [specialty, specialtiesData]);

  const searchQuery = useDebounce(search, 500);

  useEffect(() => {
    if (searchQuery !== undefined) {
      setPage(1);
      trackCommunityTemplateSearched({ search: searchQuery });
    }
  }, [searchQuery, setPage]);

  return useQueryFormTemplatesCore({
    sort,
    location,
    specialty,
    specialtyGroups,
    searchQuery,
    page,
    enabled: options?.enabled ?? true,
  });
}
